import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * Warnings strip above the bottom bar. Collapsed it shows the first warning
 * and a count; expanded it lists every warning (scrollable) so none is hidden
 * behind "and N more".
 */
export function WarningsBar({ warnings }: { warnings: string[] }) {
  const [expanded, setExpanded] = useState(false);
  if (warnings.length === 0) return null;
  const more = warnings.length - 1;
  const open = expanded && more > 0;

  return (
    <div className="shrink-0 border-t border-amber-200 bg-amber-50 text-[11.5px] text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={open}
        disabled={more === 0}
        className="flex w-full items-start gap-2 px-4 py-1 text-left disabled:cursor-default"
      >
        <span aria-hidden="true">⚠</span>
        {/* A single warning is shown in full; several collapse to the first. */}
        <span
          className={[
            "min-w-0 flex-1",
            open ? "font-semibold" : more > 0 ? "truncate" : "",
          ].join(" ")}
        >
          {open
            ? `${warnings.length} thing${warnings.length === 1 ? "" : "s"} to check before importing to Shopify`
            : warnings[0]}
        </span>
        {more > 0 && (
          <span className="flex shrink-0 items-center gap-0.5 font-medium underline-offset-2 hover:underline">
            {open ? "Hide" : `+${more} more`}
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          </span>
        )}
      </button>
      {open && (
        <ul className="max-h-[30vh] list-disc space-y-1 overflow-y-auto px-4 pb-2 pl-9">
          {warnings.map((warning, index) => (
            <li key={index} className="leading-snug">
              {warning}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
